import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Gift, X, Tag, Info } from "lucide-react";
import { toast } from "sonner";
import { SERVICES, bundleQuote, ST_LOUIS_TRAVEL_FEE } from "@/lib/pricing";
import { submitBooking, lookupGiftCard, validateDiscount, extractApiError } from "@/lib/api";

export default function BuildYourExperience() {
  const [selected, setSelected] = useState([]);
  const [step, setStep] = useState(1);
  const [eventLocation, setEventLocation] = useState("");
  const [details, setDetails] = useState({
    name: "", email: "", phone: "",
    event_date: "", event_type: "", guest_count: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [giftCodeInput, setGiftCodeInput] = useState("");
  const [giftCard, setGiftCard] = useState(null);
  const [giftLoading, setGiftLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const quote = useMemo(() => bundleQuote({ selectedKeys: selected, eventLocation }), [selected, eventLocation]);
  const customQuote = quote.customQuote;

  // Promo + gift credit only apply when we have a concrete total (single-service, no custom item).
  const promoDiscount = (!customQuote && promo) ? Math.min(promo.discount, quote.total) : 0;
  const afterPromo = customQuote ? null : Math.max((quote.total || 0) - promoDiscount, 0);
  const giftCredit = (!customQuote && giftCard) ? Math.min(Math.floor(giftCard.balance), afterPromo) : 0;
  const effectiveTotal = customQuote ? null : Math.max((afterPromo || 0) - giftCredit, 0);

  const toggle = (key) => setSelected(s => s.includes(key) ? s.filter(k => k !== key) : [...s, key]);

  const applyGift = async () => {
    const code = giftCodeInput.trim().toUpperCase();
    if (!code) return;
    setGiftLoading(true);
    try {
      const card = await lookupGiftCard(code);
      setGiftCard(card);
      toast.success(`Applied — $${Math.floor(card.balance)} credit`);
    } catch (err) {
      setGiftCard(null);
      toast.error(extractApiError(err, "Gift code not valid"));
    } finally { setGiftLoading(false); }
  };
  const removeGift = () => { setGiftCard(null); setGiftCodeInput(""); };

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    if (customQuote) { toast.error("Promo codes apply to single-service bookings. Bundles receive a custom quote."); return; }
    if (!quote.total) { toast.error("Select a service first."); return; }
    setPromoLoading(true);
    try {
      const res = await validateDiscount({ code, context: "bundle", subtotal: quote.total });
      setPromo(res);
      toast.success(`Promo applied — −$${res.discount}`);
    } catch (err) {
      setPromo(null);
      toast.error(extractApiError(err, "Promo code not valid"));
    } finally { setPromoLoading(false); }
  };
  const removePromo = () => { setPromo(null); setPromoInput(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) { toast.error("Pick at least one service."); return; }
    setSubmitting(true);
    try {
      const noteBits = [
        customQuote
          ? "Bundled inquiry — Simply Social to provide a custom quote."
          : `Single-service estimated total: $${effectiveTotal}`,
        quote.travelFee ? `St. Louis-area travel fee (+$${quote.travelFee}) included in estimate` : "",
        promo ? `Promo code ${promo.code}: −$${promoDiscount}` : "",
        giftCard ? `Gift card ${giftCard.code} applied for $${giftCredit} credit` : "",
        details.message ? `\n\nNotes: ${details.message}` : "",
      ].filter(Boolean).join(" | ");
      await submitBooking({
        name: details.name, email: details.email, phone: details.phone,
        event_date: details.event_date,
        event_location: eventLocation,
        event_type: details.event_type,
        guest_count: Number(details.guest_count) || 0,
        services: selected,
        message: noteBits,
        discount_code: promo ? promo.code : "",
        gift_card_code: giftCard ? giftCard.code : "",
        gift_credit_applied: giftCredit || 0,
        estimated_total: effectiveTotal || 0,
      });
      setDone(true);
      toast.success("Inquiry sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error(extractApiError(err, "Something went wrong"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="build-success" className="pt-40 md:pt-52 pb-32 mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>Inquiry Received</div>
        <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-8xl leading-none max-w-[16ch]">
          Let's get social.
        </h1>
        <p className="mt-8 font-body text-lg md:text-2xl max-w-3xl" style={{ color: "var(--ss-cream-dim)" }}>
          {customQuote
            ? "You'll hear from us shortly with your custom Simply Social bundle quote."
            : <>Your inquiry landed with an estimated total of <span style={{ color: "var(--ss-rust)" }}>${effectiveTotal}</span>{quote.travelFee ? " (including St. Louis travel fee)" : ""}. We'll be in touch shortly.</>
          }
        </p>
      </div>
    );
  }

  return (
    <div data-testid="build-page">
      <section className="pt-40 md:pt-52 pb-10 md:pb-16 mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Build Your Experience</div>
        <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-8xl leading-none max-w-[18ch]">
          Pick your Simply Social.
        </h1>
        <p className="mt-6 font-body text-lg md:text-xl max-w-2xl" style={{ color: "var(--ss-cream-dim)" }}>
          Choose one service and see the starting price, or combine multiple services to receive an exclusive custom bundle quote.
        </p>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-6 flex gap-6" data-testid="build-steps">
        {["Services", "Details"].map((s, i) => (
          <div key={s} className="flex items-center gap-3">
            <div className="h-6 w-6 flex items-center justify-center font-label text-[0.65rem]"
                 style={{
                   background: step >= i + 1 ? "var(--ss-rust)" : "transparent",
                   border: `1px solid ${step >= i + 1 ? "var(--ss-rust)" : "var(--ss-border)"}`,
                   color: step >= i + 1 ? "#F5F1E8" : undefined,
                 }}>{i + 1}</div>
            <span className="font-label text-xs" style={{ color: step === i + 1 ? "var(--ss-rust)" : "var(--ss-cream-dim)" }}>{s}</span>
          </div>
        ))}
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-16 grid lg:grid-cols-12 gap-10" data-testid="build-content">
        {step === 1 && (
          <>
            <div className="lg:col-span-8 space-y-6">
              <div className="p-6 border" style={{ borderColor: "var(--ss-border)" }} data-testid="build-basics">
                <div className="font-label text-xs mb-4 flex items-center gap-2" style={{ color: "var(--ss-rust)" }}>
                  <Info size={12} /> Event Location (drives travel fee)
                </div>
                <input type="text" className="editorial-input" placeholder="City, State (e.g. Chicago, IL or St. Louis, MO)"
                       data-testid="build-basics-location"
                       value={eventLocation} onChange={(e) => setEventLocation(e.target.value)} />
                {quote.stLouis && (
                  <div className="mt-3 text-xs font-label" style={{ color: "var(--ss-gold)" }} data-testid="build-stlouis-flag">
                    St. Louis area detected — +${ST_LOUIS_TRAVEL_FEE} travel fee will apply.
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {SERVICES.map(s => {
                  const active = selected.includes(s.key);
                  return (
                    <button
                      type="button"
                      key={s.key}
                      data-testid={`build-service-${s.key}`}
                      onClick={() => toggle(s.key)}
                      className="text-left group relative overflow-hidden border transition-colors"
                      style={{
                        borderColor: active ? "var(--ss-rust)" : "var(--ss-border)",
                        background: "var(--ss-bg-2)",
                      }}>
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img src={s.img} alt={s.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,14,12,0.15), rgba(15,14,12,0.7))" }} />
                        <div className="absolute top-4 left-4 font-label text-xs" style={{ color: "var(--ss-gold)" }}>{s.tag}.</div>
                        {active && (
                          <div className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center" style={{ background: "var(--ss-rust)" }}>
                            <Check size={16} color="#F5F1E8" />
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-baseline justify-between">
                          <div className="font-display text-2xl">{s.name}</div>
                          <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>{s.priceLabel}</div>
                        </div>
                        <div className="mt-2 font-body text-sm" style={{ color: "var(--ss-cream-dim)" }}>{s.detail}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-32 p-8 border" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }} data-testid="build-summary">
                <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Your Selection</div>
                <div className="mt-2 font-display text-3xl">{customQuote ? "Custom Quote" : "Live Quote"}</div>

                {quote.selected.length === 0 && (
                  <p className="mt-6 font-body text-sm" style={{ color: "var(--ss-cream-dim)" }}>Select a service to see your quote.</p>
                )}

                {quote.selected.length > 0 && (
                  <div className="mt-6 space-y-3">
                    {quote.selected.map(s => (
                      <div key={s.key} className="flex justify-between font-body text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                        <span>{s.name}</span>
                        <span style={{ color: "var(--ss-cream)" }}>{s.price ? `from $${s.price}` : "custom"}</span>
                      </div>
                    ))}

                    {customQuote ? (
                      <div className="mt-6 p-5 border" style={{ borderColor: "var(--ss-gold)", background: "rgba(184,150,43,0.08)" }} data-testid="build-custom-quote-note">
                        <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-gold)" }}>Simply Social Bundle</div>
                        <p className="mt-2 font-body text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                          Exclusive bundled pricing available. Request a custom quote — we'll price the full experience for you.
                        </p>
                        {quote.stLouis && (
                          <p className="mt-3 text-xs" style={{ color: "var(--ss-gold)" }}>
                            St. Louis events include a +${ST_LOUIS_TRAVEL_FEE} travel fee.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="border-t pt-3 mt-3 space-y-2" style={{ borderColor: "var(--ss-border)" }}>
                        <div className="flex justify-between text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                          <span>Starting price</span><span>${quote.subtotal}</span>
                        </div>
                        {quote.travelFee > 0 && (
                          <div className="flex justify-between text-sm" style={{ color: "var(--ss-gold)" }}>
                            <span>St. Louis travel fee</span><span>+${quote.travelFee}</span>
                          </div>
                        )}
                        {promo && (
                          <div className="flex justify-between text-sm" style={{ color: "var(--ss-gold)" }}>
                            <span>Promo ({promo.code})</span><span>− ${promoDiscount}</span>
                          </div>
                        )}
                        {giftCard && (
                          <div className="flex justify-between text-sm" style={{ color: "var(--ss-gold)" }} data-testid="build-gift-line">
                            <span>Gift credit ({giftCard.code})</span><span>− ${giftCredit}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-baseline pt-3">
                          <div className="font-label text-xs" style={{ color: "var(--ss-cream-dim)" }}>Estimated total</div>
                          <div className="font-display text-4xl" style={{ color: "var(--ss-rust)" }} data-testid="build-effective-total">
                            from ${effectiveTotal}
                          </div>
                        </div>
                      </div>
                    )}

                    {!customQuote && (
                      <div className="mt-6 pt-6 border-t space-y-5" style={{ borderColor: "var(--ss-border)" }}>
                        <div>
                          <div className="font-label text-xs mb-3 flex items-center gap-2" style={{ color: "var(--ss-cream-dim)" }}>
                            <Tag size={12} /> Promo Code
                          </div>
                          {!promo ? (
                            <div className="flex gap-2">
                              <input data-testid="build-promo-input" className="editorial-input flex-1" placeholder="e.g. LAUNCH10"
                                     value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }} />
                              <button type="button" data-testid="build-promo-apply" onClick={applyPromo} disabled={promoLoading || !promoInput.trim()}
                                      className="font-label text-xs px-4 py-2 border transition-colors disabled:opacity-40"
                                      style={{ borderColor: "var(--ss-rust)", color: "var(--ss-rust)" }}>
                                {promoLoading ? "…" : "Apply"}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-2 border p-3" data-testid="build-promo-applied"
                                 style={{ borderColor: "var(--ss-gold)", background: "rgba(184,150,43,0.08)" }}>
                              <div className="min-w-0">
                                <div className="font-display text-lg tracking-widest truncate">{promo.code}</div>
                                <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-gold)" }}>−${promoDiscount}</div>
                              </div>
                              <button type="button" onClick={removePromo} data-testid="build-promo-remove" className="p-1"><X size={16} /></button>
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="font-label text-xs mb-3 flex items-center gap-2" style={{ color: "var(--ss-cream-dim)" }}>
                            <Gift size={12} /> Gift Card Code
                          </div>
                          {!giftCard ? (
                            <div className="flex gap-2">
                              <input data-testid="build-gift-input" className="editorial-input flex-1" placeholder="SS-XXXX-XXXX"
                                     value={giftCodeInput} onChange={(e) => setGiftCodeInput(e.target.value.toUpperCase())}
                                     onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyGift(); } }} />
                              <button type="button" data-testid="build-gift-apply" onClick={applyGift} disabled={giftLoading || !giftCodeInput.trim()}
                                      className="font-label text-xs px-4 py-2 border transition-colors disabled:opacity-40"
                                      style={{ borderColor: "var(--ss-rust)", color: "var(--ss-rust)" }}>
                                {giftLoading ? "…" : "Apply"}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-2 border p-3" data-testid="build-gift-applied"
                                 style={{ borderColor: "var(--ss-gold)", background: "rgba(184,150,43,0.08)" }}>
                              <div className="min-w-0">
                                <div className="font-display text-lg tracking-widest truncate">{giftCard.code}</div>
                                <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-gold)" }}>${giftCredit} credit applied</div>
                              </div>
                              <button type="button" onClick={removeGift} data-testid="build-gift-remove" className="p-1"><X size={16} /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button type="button" onClick={() => setStep(2)} disabled={selected.length === 0}
                        data-testid="build-continue-btn"
                        className="btn-primary mt-8 w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                  Continue <ArrowUpRight className="arrow" size={14} />
                </button>
                <p className="mt-4 font-body text-[0.7rem]" style={{ color: "var(--ss-muted)" }}>
                  Prices shown are the current Simply Social starting rates. Final pricing tailored to your exact scope. St. Louis-area events include a +${ST_LOUIS_TRAVEL_FEE} travel fee.
                </p>
              </div>
            </aside>
          </>
        )}

        {step === 2 && (
          <>
            <form onSubmit={submit} className="lg:col-span-8 space-y-8" data-testid="build-details-form">
              <div className="grid sm:grid-cols-2 gap-8">
                <input required data-testid="build-name" className="editorial-input" placeholder="Full Name"
                       value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} />
                <input required type="email" data-testid="build-email" className="editorial-input" placeholder="Email"
                       value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} />
                <input required data-testid="build-phone" className="editorial-input" placeholder="Phone"
                       value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} />
                <div className="flex flex-col">
                  <label htmlFor="build-date" className="font-label text-[0.6rem] mb-2" style={{ color: "var(--ss-rust)", letterSpacing: "2px" }}>
                    Event Date
                  </label>
                  <input required id="build-date" type="date" data-testid="build-date" className="editorial-input"
                         value={details.event_date} onChange={(e) => setDetails({ ...details, event_date: e.target.value })} />
                </div>
                <input required data-testid="build-type" className="editorial-input md:col-span-2" placeholder="Event Type (Wedding, Corporate…)"
                       value={details.event_type} onChange={(e) => setDetails({ ...details, event_type: e.target.value })} />
                <input required type="number" min="1" data-testid="build-guests" className="editorial-input md:col-span-2" placeholder="Estimated guest count"
                       value={details.guest_count} onChange={(e) => setDetails({ ...details, guest_count: e.target.value })} />
              </div>
              <div className="p-4 border text-sm flex flex-wrap gap-x-6 gap-y-2" style={{ borderColor: "var(--ss-border)", color: "var(--ss-cream-dim)" }}>
                <span><span style={{ color: "var(--ss-muted)" }}>Location:</span> {eventLocation || "—"}</span>
                <button type="button" onClick={() => setStep(1)} className="underline hover:text-[color:var(--ss-rust)]">Edit</button>
              </div>
              <div>
                <label className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Anything else we should know?</label>
                <textarea rows={5} data-testid="build-message" className="editorial-input"
                          placeholder="Vibe, must-haves, big ideas…"
                          value={details.message} onChange={(e) => setDetails({ ...details, message: e.target.value })} />
              </div>
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => setStep(1)} data-testid="build-back-btn" className="btn-outline">Back</button>
                <button type="submit" disabled={submitting} data-testid="build-submit" className="btn-primary">
                  {submitting ? "Sending…" : (customQuote ? "Request Custom Quote" : "Submit Inquiry")} <ArrowUpRight className="arrow" size={14} />
                </button>
              </div>
            </form>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-32 p-8 border" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}>
                <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Your Selection</div>
                <div className="mt-4 space-y-2">
                  {quote.selected.map(s => (
                    <div key={s.key} className="flex justify-between text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                      <span>{s.name}</span>
                      <span style={{ color: "var(--ss-cream)" }}>{s.price ? `from $${s.price}` : "custom"}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 border-t pt-4" style={{ borderColor: "var(--ss-border)" }}>
                  {customQuote ? (
                    <p className="text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                      Exclusive bundled pricing available. We'll send you a custom quote after receiving your inquiry.
                    </p>
                  ) : (
                    <>
                      {quote.travelFee > 0 && (
                        <div className="flex justify-between mt-1 text-sm" style={{ color: "var(--ss-gold)" }}>
                          <span>St. Louis travel fee</span><span>+${quote.travelFee}</span>
                        </div>
                      )}
                      {promo && (
                        <div className="flex justify-between mt-1 text-sm" style={{ color: "var(--ss-gold)" }}>
                          <span>Promo</span><span>− ${promoDiscount}</span>
                        </div>
                      )}
                      {giftCard && (
                        <div className="flex justify-between mt-1 text-sm" style={{ color: "var(--ss-gold)" }}>
                          <span>Gift credit</span><span>− ${giftCredit}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-baseline mt-3">
                        <div className="font-label text-xs" style={{ color: "var(--ss-cream-dim)" }}>Estimated total</div>
                        <div className="font-display text-4xl" style={{ color: "var(--ss-rust)" }}>from ${effectiveTotal}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </aside>
          </>
        )}
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-32 text-center">
        <p className="font-body text-sm" style={{ color: "var(--ss-muted)" }}>
          Prefer to just tell us about your event? <Link to="/book" className="underline hover:text-[color:var(--ss-rust)]">Open inquiry form.</Link>
        </p>
      </section>
    </div>
  );
}
